import { get, post } from 'aws-amplify/api';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  updateUserAttribute as amplifyUpdateUserAttribute,
} from 'aws-amplify/auth';
import { Role, UserGroup } from 'API';
import PersonService from 'services/person';

// User-facing names for each role
export const roleToString = (role: Role): string => {
  switch (role) {
    case Role.ADMIN:
      return 'Administrator';

    case Role.TEAMMEMBER:
      return 'Team member';

    case Role.FOREIGNNATIONAL:
      return 'Foreign national';

    case Role.EMPLOYER:
      return 'Employer';

    default:
      return 'Foreign national';
  }
};

export const ALL_ROLES: Array<Role> = [
  Role.ADMIN,
  Role.TEAMMEMBER,
  Role.EMPLOYER,
  Role.FOREIGNNATIONAL];

export const MH_ROLES: Array<Role> = [Role.ADMIN, Role.TEAMMEMBER];

export const MH_EMPLOYER_ROLES: Array<Role> = [
  Role.ADMIN,
  Role.TEAMMEMBER,
  Role.EMPLOYER,
];

export const MH_FN: Array<Role> = [
  Role.ADMIN,
  Role.TEAMMEMBER,
  Role.FOREIGNNATIONAL,
];

export const EM_FN_ROLES: Array<Role> = [
  Role.EMPLOYER,
  Role.FOREIGNNATIONAL,
];

interface CreateUserInput {
  companyAccess?: Array<string>,
  companyAdmin?: Array<string>,
  email: string,
  personID: string,
  role: Role,
  suppressMessage?: boolean
}

interface EmailCheckReturn {
  email: string,
  found: boolean,
  personID?: string | null,
  type?: string | null
}

interface ResendInviteResponse {
  message: string;
  success: boolean;
}

class AuthService {
  private apiName = 'AdminQueries';

  private getAccessToken = async (): Promise<string> => {
    const session = await fetchAuthSession();

    return session.tokens?.accessToken?.toString() ?? '';
  };

  public getAuthUser = () => getCurrentUser().catch((err) => {
    throw err;
  });

  public getCurrentIdentityID = async (): Promise<string> => {
    const session = await fetchAuthSession();

    return session.identityId ?? '';
  };

  public getUserToken = async () => {
    try {
      const session = await fetchAuthSession();

      return session.tokens?.idToken?.toString() ?? false;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('get user token ', err);

      return false;
    }
  };

  checkEmail = async (email: string): Promise<EmailCheckReturn> => {
    try {
      const { body: responseBody } = await get({
        apiName: 'utilitiesapi',
        path: '/byemail',
        options: { queryParams: { email } },
      }).response;

      return await responseBody.json() as unknown as EmailCheckReturn;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  createUser = async ({
    companyAccess,
    companyAdmin,
    email,
    personID,
    role,
    suppressMessage,
  }: CreateUserInput) => {
    const authorization = await this.getAccessToken();

    try {
      const { body: responseBody } = await post({
        apiName: this.apiName,
        path: '/createUser',
        options: {
          body: {
            email,
            personID,
            role,
            ...(suppressMessage && { suppressMessage }),
            ...(companyAccess && { companyAccess }),
            ...(companyAdmin && { companyAdmin }),
          },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;

      return await responseBody.json() as unknown as { code?: string };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('create user error', err);

      throw err;
    }
  };

  getRoleForCurrentUser = async (): Promise<Role | null> => {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens) return null;

      const groups: string[] = (session.tokens.accessToken.payload['cognito:groups'] as string[]) ?? [];

      if (groups.includes('Admins') && groups.includes('Team')) return Role.ADMIN;
      if (groups.includes('Team')) return Role.TEAMMEMBER as Role;
      if (groups.includes('Employers')) return Role.EMPLOYER;
      if (groups.includes('Nationals')) return Role.FOREIGNNATIONAL;

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  /**
   * Fetch an array of IDs of companies for which the current
   * user can access and admin, as verified by Cognito.
   * @returns {Array<string>}
   */
  getCompaniesCurrentUserCanAccess = async (): Promise<{
    accessCompanies: Array<string>;
    adminCompanies: Array<string>;
  }> => {
    try {
      const attributes = await fetchUserAttributes();

      const accessCompanies = attributes['custom:company_access']
        ? JSON.parse(attributes['custom:company_access'])
        : [];

      const adminCompanies = attributes['custom:company_admin']
        ? JSON.parse(attributes['custom:company_admin'])
        : [];

      return { accessCompanies, adminCompanies };
    } catch {
      return { accessCompanies: [], adminCompanies: [] };
    }
  };

  deleteUser = async (username: string) => {
    const authorization = await this.getAccessToken();

    try {
      const { body: responseBody } = await post({
        apiName: 'AdminQueries',
        path: '/deleteUser',
        options: {
          body: { username },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;

      await responseBody.json();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw err;
    }
  };

  disableUser = async (username: string) => {
    const authorization = await this.getAccessToken();

    try {
      const { body: responseBody } = await post({
        apiName: 'AdminQueries',
        path: '/disableUser',
        options: {
          body: { username },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;

      await responseBody.json();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw err;
    }
  };

  enableUser = async (username: string) => {
    const authorization = await this.getAccessToken();

    try {
      const { body: responseBody } = await post({
        apiName: 'AdminQueries',
        path: '/enableUser',
        options: {
          body: { username },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;

      await responseBody.json();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw err;
    }
  };

  getGroupsForUser = async (username: string): Promise<Array<string>> => {
    const authorization = await this.getAccessToken();

    const { body: responseBody } = await get({
      apiName: 'AdminQueries',
      path: '/listGroupsForUser',
      options: {
        queryParams: { username },
        headers: { 'Content-Type': 'application/json', Authorization: authorization },
      },
    }).response;

    const response = await responseBody.json() as any;

    return response.Groups.map((group: any) => group.GroupName);
  };

  /** Given role, return user group.
   *  Return Team for all team members, even admins */
  roleToUserGroup = (role: Role): UserGroup => {
    if (MH_ROLES.includes(role)) {
      return UserGroup.Team;
    }
    if (role === Role.EMPLOYER) {
      return UserGroup.Employers;
    }
    if (role === Role.FOREIGNNATIONAL) {
      return UserGroup.Nationals;
    }
    throw new Error();
  };

  signUp = async (email: string, password: string, attributes: any = {}) => {
    try {
      await amplifySignUp({
        username: email,
        password,
        options: { userAttributes: attributes },
      });

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error signing up:', error);

      return false;
    }
  };

  signIn = async (email: string, password: string) => {
    try {
      const result = await amplifySignIn({ username: email, password });

      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error signing in', error);

      return false;
    }
  };

  signOut = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      PersonService.setPersonID('');
      PersonService.setPersonType(undefined);

      amplifySignOut();
      window.history.pushState({}, '', window.location.origin);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error signing out: ', error);
    }
  };

  resendInvite = async (email: string): Promise<ResendInviteResponse> => {
    const authorization = await this.getAccessToken();

    try {
      const { body: responseBody } = await post({
        apiName: 'AdminQueries',
        path: '/resendUser',
        options: {
          body: { email },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;

      return await responseBody.json() as unknown as ResendInviteResponse;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw err;
    }
  };

  sendConfirmationCode = async (email: string, code: string) => {
    try {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Verification code does not match.', code);

      return false;
    }
  };

  // Changing one's own email leaves email_verified false, so Cognito mails a
  // code to the new address and the caller gets nextStep back to say so.
  // Errors must reach the caller - swallowing them showed a saved email that
  // Cognito had actually rejected.
  updateSelfEmail = async (email: string) => amplifyUpdateUserAttribute({
    userAttribute: { attributeKey: 'email', value: email },
  });

  updateSelfAttribute = async (name: string, value: string) => {
    try {
      await amplifyUpdateUserAttribute({ userAttribute: { attributeKey: name as any, value } });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  updateUserAttribute = async (
    username: string,
    name: string,
    value: string,
  ) => {
    const authorization = await this.getAccessToken();

    try {
      // The body is deliberately left unread: the route answers an update that
      // changes nothing with an empty 200, and parsing that as JSON would throw
      // and be reported as a failed save. A non-2xx rejects the response itself.
      await post({
        apiName: 'AdminQueries',
        path: '/updateUserAttribute',
        options: {
          body: { username, name, value },
          headers: { 'Content-Type': 'application/json', Authorization: authorization },
        },
      }).response;
    } catch (err: any) {
      // AdminQueries answers failures with a JSON { message } body (duplicate
      // email, caller not an admin). Surface that reason instead of swallowing
      // it, or the modal reports a save that never reached Cognito.
      const detail = err?.response?.body;
      let message = err?.message;

      if (typeof detail === 'string' && detail.length > 0) {
        try {
          message = JSON.parse(detail)?.message || message;
        } catch {
          message = detail;
        }
      }

      throw new Error(message);
    }
  };
}

export default new AuthService();
